import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { formatDate } from '@/lib/constants';
import { useAuth } from '@/contexts/AuthContext';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Package, Truck, CheckCircle2, Trash2, Check, MessageSquare, Send, Camera, Download, AlertCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_MAP = {
  in_production: { label: 'En production', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  verification_requested: { label: 'Demande de vérification', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  shipped: { label: 'Expédié', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  delivered: { label: 'Livré', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  received: { label: 'Reçue', color: 'bg-teal-500/10 text-teal-400 border-teal-500/20' },
};

const CARRIER_LINKS = {
  DHL: (tn) => `https://www.dhl.com/fr-fr/home/tracking.html?tracking-id=${tn}`,
  FEDEX: (tn) => `https://www.fedex.com/fedextrack/?trknbr=${tn}`,
};

export default function ShipmentsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [viewDialog, setViewDialog] = useState(null);
  const [messages, setMessages] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [newMsg, setNewMsg] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const params = filterStatus ? `?status=${filterStatus}` : '';
      const { data } = await api.get(`/shipments${params}`);
      setShipments(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openView = async (s) => {
    setViewDialog(s);
    setNewMsg('');
    try {
      const [shipRes, filesRes] = await Promise.all([
        api.get(`/shipments/${s.id}`),
        api.get(`/files/list?entity_type=shipment&entity_id=${s.id}`),
      ]);
      setMessages(shipRes.data.messages || []);
      setPhotos(filesRes.data || []);
    } catch { toast.error('Erreur de chargement'); }
  };

  const sendMsg = async () => {
    if (!newMsg.trim()) return;
    try {
      const { data } = await api.post(`/shipments/${viewDialog.id}/messages`, { text: newMsg });
      setMessages(prev => [...prev, data]);
      setNewMsg('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const downloadPhoto = async (file) => {
    try {
      const res = await api.get(`/files/download/${file.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Erreur'); }
  };

  const handleMarkReceived = async (s) => {
    if (!window.confirm(`Marquer "${s.project_name}" comme reçue ?`)) return;
    try {
      await api.put(`/shipments/${s.id}`, { status: 'received' });
      toast.success('Expédition marquée comme reçue');
      fetchData();
    } catch { toast.error('Erreur lors de la mise à jour'); }
  };

  const handleDelete = async (s) => {
    const isPendingApproval = s.deletion_requested;
    const msg = isPendingApproval
      ? `Confirmer la suppression demandée par ${s.deletion_requested_by || s.subcontractor_name} ?\n\nRaison : ${s.deletion_reason || '(aucune)'}\n\nCette action est irréversible.`
      : `Supprimer définitivement l'expédition "${s.project_name}" ?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/shipments/${s.id}`);
      toast.success('Expédition supprimée');
      fetchData();
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  const handleRejectDeletion = async (s) => {
    if (!window.confirm(`Refuser la demande de suppression de ${s.deletion_requested_by || s.subcontractor_name} ?`)) return;
    try {
      await api.post(`/shipments/${s.id}/cancel-deletion`);
      toast.success('Demande de suppression refusée');
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  return (
    <div className="space-y-6" data-testid="shipments-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-zinc-50 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Expéditions</h1>
          <p className="text-sm text-zinc-500 mt-1">{shipments.length} expédition(s)</p>
        </div>
      </div>

      <div className="flex gap-2">
        <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-40 bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm h-9 text-sm"><SelectValue placeholder="Tous" /></SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="in_production">En production</SelectItem>
            <SelectItem value="shipped">Expédié</SelectItem>
            <SelectItem value="delivered">Livré</SelectItem>
            <SelectItem value="received">Reçue</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Sous-traitant</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Projet</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Statut</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Date fin estimée</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Transporteur</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium">N° Suivi</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Notes</TableHead>
              {isAdmin && <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i} className="border-zinc-800/50">
                  {[...Array(isAdmin ? 8 : 7)].map((__, j) => <TableCell key={j}><div className="h-4 nexus-shimmer rounded w-20" /></TableCell>)}
                </TableRow>
              ))
            ) : shipments.length === 0 ? (
              <TableRow><TableCell colSpan={isAdmin ? 8 : 7} className="text-center py-12 text-zinc-500">Aucune expédition</TableCell></TableRow>
            ) : shipments.map(s => (
              <TableRow key={s.id} className={`border-zinc-800/50 nexus-table-row ${s.deletion_requested ? 'bg-rose-500/5' : ''}`}>
                <TableCell className="text-sm text-zinc-200 font-medium">
                  {s.subcontractor_name}
                  {s.deletion_requested && (
                    <div className="text-[10px] text-rose-400 mt-0.5 flex items-center gap-1" data-testid={`deletion-pending-${s.id}`}>
                      <AlertCircle className="w-3 h-3" /> Demande de suppression en attente
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-sm text-zinc-300">{s.project_name}</TableCell>
                <TableCell>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-sm text-xs font-medium border ${STATUS_MAP[s.status]?.color || 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'}`}>
                    {s.status === 'shipped' && <Truck className="w-3 h-3 mr-1" />}
                    {s.status === 'in_production' && <Package className="w-3 h-3 mr-1" />}
                    {s.status === 'received' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                    {STATUS_MAP[s.status]?.label || s.status}
                  </span>
                  {s.status === 'received' && s.received_date && (
                    <div className="text-xs text-zinc-500 mt-1">le {formatDate(s.received_date)}</div>
                  )}
                  {s.status === 'received' && s.auto_delete_in_days !== undefined && (
                    <div className={`text-[10px] mt-0.5 ${s.auto_delete_in_days <= 3 ? 'text-rose-400' : 'text-zinc-600'}`} data-testid={`auto-delete-badge-${s.id}`}>
                      {s.auto_delete_in_days === 0
                        ? 'Suppression auto. à minuit'
                        : `Suppression auto. dans ${s.auto_delete_in_days}j`}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-sm text-zinc-400">{formatDate(s.estimated_end_date)}</TableCell>
                <TableCell className="text-sm text-zinc-300 font-medium">{s.carrier}</TableCell>
                <TableCell>
                  {s.tracking_number ? (
                    CARRIER_LINKS[s.carrier] ? (
                      <a href={CARRIER_LINKS[s.carrier](s.tracking_number)} target="_blank" rel="noreferrer" className="text-sm text-blue-400 hover:text-blue-300 font-mono underline">
                        {s.tracking_number}
                      </a>
                    ) : (
                      <span className="text-sm text-zinc-300 font-mono">{s.tracking_number}</span>
                    )
                  ) : <span className="text-zinc-600">-</span>}
                </TableCell>
                <TableCell className="text-sm text-zinc-500 max-w-xs truncate">{s.notes || '-'}</TableCell>
                {isAdmin && (
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {s.deletion_requested && (
                        <>
                          <button
                            data-testid={`reject-deletion-${s.id}`}
                            onClick={() => handleRejectDeletion(s)}
                            title={`Refuser la demande de suppression\nRaison: ${s.deletion_reason || '(aucune)'}`}
                            className="p-1.5 text-zinc-500 hover:text-amber-400 transition-colors">
                            <XCircle className="w-4 h-4" />
                          </button>
                          <button
                            data-testid={`approve-deletion-${s.id}`}
                            onClick={() => handleDelete(s)}
                            title={`Confirmer la suppression demandée par ${s.deletion_requested_by || s.subcontractor_name}`}
                            className="p-1.5 text-rose-400 hover:text-rose-300 transition-colors animate-pulse">
                            <CheckCircle2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      <button
                        data-testid={`view-prod-${s.id}`}
                        onClick={() => openView(s)}
                        title="Photos & échanges"
                        className="p-1.5 text-zinc-500 hover:text-amber-400 transition-colors">
                        <MessageSquare className="w-4 h-4" />
                        {(s.messages || []).length > 0 && <span className="text-[9px] text-amber-400 ml-0.5">{s.messages.length}</span>}
                      </button>
                      {s.status !== 'received' && !s.deletion_requested && (
                        <button
                          data-testid={`mark-received-${s.id}`}
                          onClick={() => handleMarkReceived(s)}
                          title="Marquer comme reçue"
                          className="p-1.5 text-zinc-500 hover:text-teal-400 transition-colors">
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      {!s.deletion_requested && (
                        <button
                          data-testid={`delete-shipment-${s.id}`}
                          onClick={() => handleDelete(s)}
                          title="Supprimer"
                          className="p-1.5 text-zinc-500 hover:text-rose-400 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* View Dialog: photos + messages */}
      <Dialog open={!!viewDialog} onOpenChange={() => setViewDialog(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="prod-view-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{viewDialog?.project_name}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              Sous-traitant: <span className="text-zinc-300">{viewDialog?.subcontractor_name}</span> · Statut: <span className="text-zinc-300">{STATUS_MAP[viewDialog?.status]?.label || viewDialog?.status}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-5">
            {/* Photos */}
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm flex items-center gap-1.5"><Camera className="w-4 h-4" /> Photos / Documents ({photos.length})</Label>
              {photos.length === 0 ? (
                <p className="text-xs text-zinc-600 text-center py-4 bg-zinc-950 border border-zinc-800 border-dashed rounded-sm">Aucune photo envoyée par le sous-traitant.</p>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                  {photos.map(f => <AdminPhotoThumb key={f.id} file={f} onDownload={() => downloadPhoto(f)} />)}
                </div>
              )}
            </div>
            {/* Chat */}
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm flex items-center gap-1.5"><MessageSquare className="w-4 h-4" /> Échanges ({messages.length})</Label>
              <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-3 space-y-2 max-h-72 overflow-y-auto" data-testid="admin-messages-list">
                {messages.length === 0 ? (
                  <p className="text-xs text-zinc-600 text-center py-4">Aucun message.</p>
                ) : messages.map(m => {
                  const isAdminMsg = m.sender_role === 'admin';
                  return (
                    <div key={m.id} className={`flex ${isAdminMsg ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] px-3 py-1.5 rounded-sm text-sm border ${isAdminMsg ? 'bg-amber-500/10 text-amber-100 border-amber-500/20' : 'bg-zinc-900 text-zinc-200 border-zinc-800'}`}>
                        <p className="text-[10px] text-zinc-500 mb-0.5">
                          <span className={isAdminMsg ? 'text-amber-400' : 'text-zinc-400'}>{m.sender_name}</span>
                          <span className="ml-2">{formatDate(m.created_at)}</span>
                        </p>
                        <p className="whitespace-pre-wrap">{m.text}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-2">
                <Input data-testid="admin-msg-input" value={newMsg} onChange={(e) => setNewMsg(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendMsg())}
                  placeholder="Répondre au sous-traitant..." className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
                <Button data-testid="admin-send-msg" onClick={sendMsg} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm"><Send className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialog(null)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AdminPhotoThumb({ file, onDownload }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/files/download/${file.id}`, { responseType: 'blob' })
      .then(res => { if (!cancelled) setSrc(URL.createObjectURL(res.data)); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.id]);
  const isImage = (file.content_type || '').startsWith('image/');
  return (
    <button onClick={onDownload} className="relative bg-zinc-950 border border-zinc-800 rounded-sm overflow-hidden aspect-square hover:border-amber-500/30 transition-colors group">
      {isImage && src ? (
        <img src={src} alt={file.original_filename} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center p-2">
          <Download className="w-6 h-6 text-zinc-500 mb-1" />
          <p className="text-[10px] text-zinc-400 text-center truncate w-full">{file.original_filename}</p>
        </div>
      )}
      <div className="absolute inset-0 bg-zinc-950/0 group-hover:bg-zinc-950/40 flex items-center justify-center transition-colors">
        <Download className="w-5 h-5 text-zinc-100 opacity-0 group-hover:opacity-100" />
      </div>
    </button>
  );
}
